import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Calculator, Plus, Trash2, Search, X, Save, Loader2, AlertTriangle, Copy, FolderOpen, FileText, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Empty row template
const createEmptyRow = (id) => ({
  id,
  antal: 1,
  articleNumber: "",
  productName: "",
  brand: "",
  kategori: "",
  inkopspris: 0,
  frakt: 0,
  kemskatt: 0,
  bestallningsstalle: "",
  pris: 0,
  productId: null,
  ean: null,
});

export default function QuoteCalculator() {
  const [rows, setRows] = useState([createEmptyRow(1)]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [openPopover, setOpenPopover] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const saveTimeoutRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openPopover && !e.target.closest('[data-quote-search]')) {
        setOpenPopover(null);
        setSearchQuery("");
        setSearchResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openPopover]);
  
  // Saved quotes state
  const [savedQuotes, setSavedQuotes] = useState([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [quoteName, setQuoteName] = useState("");
  const [currentQuoteId, setCurrentQuoteId] = useState(null);
  const [currentQuoteName, setCurrentQuoteName] = useState("");

  // Load saved quote on mount
  useEffect(() => {
    loadQuote();
  }, []);

  // Auto-save when rows change (debounced)
  useEffect(() => {
    if (loading) return; // Don't save while loading
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set new timeout for auto-save
    saveTimeoutRef.current = setTimeout(() => {
      saveQuote();
    }, 1000); // Save 1 second after last change
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [rows, loading]);

  const loadQuote = async () => {
    try {
      const res = await axios.get(`${API}/quotes/my`);
      if (res.data.rows && res.data.rows.length > 0) {
        // Enrich rows with missing kategori/brand from inventory
        const enrichedRows = await enrichRowsWithProductData(res.data.rows);
        setRows(enrichedRows);
      }
    } catch (error) {
      console.error("Error loading quote:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch missing product details (kategori, brand) for saved rows
  const enrichRowsWithProductData = async (rows) => {
    const rowsNeedingEnrichment = rows.filter(
      row => row.productId && (!row.kategori || !row.brand)
    );
    
    if (rowsNeedingEnrichment.length === 0) return rows;
    
    // Fetch product data for rows missing kategori/brand
    const enrichedRows = await Promise.all(
      rows.map(async (row) => {
        if (!row.productId || (row.kategori && row.brand)) {
          return row;
        }
        
        try {
          // Search by articleNumber or EAN
          const searchTerm = row.articleNumber || row.ean;
          if (!searchTerm) return row;
          
          const res = await axios.get(`${API}/inventory?search=${encodeURIComponent(searchTerm)}&limit=1`);
          const items = res.data.items || [];
          
          if (items.length > 0) {
            const product = items[0];
            return {
              ...row,
              kategori: row.kategori || product.kategori || "",
              brand: row.brand || product.tillverkare || "",
            };
          }
        } catch (error) {
          console.error("Error enriching row:", error);
        }
        return row;
      })
    );
    
    return enrichedRows;
  };

  const saveQuote = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/quotes/my`, { rows });
    } catch (error) {
      console.error("Error saving quote:", error);
      toast.error("Kunde inte spara offerten");
    } finally {
      setSaving(false);
    }
  };

  // Load list of saved quotes
  const loadSavedQuotes = async () => {
    try {
      const res = await axios.get(`${API}/quotes/saved`);
      setSavedQuotes(res.data || []);
    } catch (error) {
      console.error("Error loading saved quotes:", error);
    }
  };

  // Save quote with name
  const saveQuoteWithName = async () => {
    if (!quoteName.trim()) {
      toast.error("Ange ett namn för offerten");
      return;
    }
    
    const filledRows = rows.filter(row => row.productId);
    if (filledRows.length === 0) {
      toast.error("Inga produkter att spara");
      return;
    }
    
    try {
      if (currentQuoteId) {
        // Update existing quote
        await axios.put(`${API}/quotes/saved/${currentQuoteId}`, {
          name: quoteName.trim(),
          rows: filledRows
        });
        toast.success("Offerten uppdaterad");
      } else {
        // Create new quote
        const res = await axios.post(`${API}/quotes/saved`, {
          name: quoteName.trim(),
          rows: filledRows
        });
        setCurrentQuoteId(res.data.id);
        toast.success("Offerten sparad");
      }
      setCurrentQuoteName(quoteName.trim());
      setSaveDialogOpen(false);
      loadSavedQuotes();
    } catch (error) {
      console.error("Error saving quote:", error);
      toast.error("Kunde inte spara offerten");
    }
  };

  // Load a saved quote
  const loadSavedQuote = async (quote) => {
    try {
      const enrichedRows = await enrichRowsWithProductData(quote.rows);
      setRows(enrichedRows.length > 0 ? enrichedRows : [createEmptyRow(1)]);
      setCurrentQuoteId(quote.id);
      setCurrentQuoteName(quote.name);
      setQuoteName(quote.name);
      setLoadDialogOpen(false);
      toast.success(`Offert "${quote.name}" laddad`);
    } catch (error) {
      console.error("Error loading saved quote:", error);
      toast.error("Kunde inte ladda offerten");
    }
  };

  // Delete a saved quote
  const deleteSavedQuote = async (quoteId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/quotes/saved/${quoteId}`);
      setSavedQuotes(prev => prev.filter(q => q.id !== quoteId));
      if (currentQuoteId === quoteId) {
        setCurrentQuoteId(null);
        setCurrentQuoteName("");
        setQuoteName("");
      }
      toast.success("Offerten raderad");
    } catch (error) {
      console.error("Error deleting quote:", error);
      toast.error("Kunde inte radera offerten");
    }
  };

  // Create new quote (clear current)
  const createNewQuote = () => {
    setRows([createEmptyRow(1)]);
    setCurrentQuoteId(null);
    setCurrentQuoteName("");
    setQuoteName("");
    toast.success("Ny offert skapad");
  };

  // Search products using API
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const res = await axios.get(`${API}/inventory?search=${encodeURIComponent(query)}&limit=15`);
      const items = res.data.items || [];
      setSearchResults(items);
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Select a product for a row - fetch price from API
  const handleSelectProduct = async (rowId, product) => {
    // Get product name and details
    const productName = product.produktnamn || product.name || "";
    const articleNumber = product.artikelnummer || product.article_number || product.ean || "";
    const brand = product.tillverkare || product.brand || "";
    const kategori = product.kategori || product.category || "";
    const ean = product.ean || "";
    
    // Close popover immediately for better UX
    setOpenPopover(null);
    setSearchQuery("");
    setSearchResults([]);
    
    // Update row with basic info first
    setRows(prev => prev.map(row => {
      if (row.id !== rowId) return row;
      return {
        ...row,
        productId: product.id,
        articleNumber,
        productName,
        brand,
        kategori,
        ean,
        inkopspris: 0, // Will be updated below
        frakt: 0,
        kemskatt: 0,
        bestallningsstalle: "",
      };
    }));
    
    // Fetch price from API
    try {
      const priceRes = await axios.get(`${API}/inventory/${product.id}/price`);
      const priceData = priceRes.data;
      const EUR_TO_SEK = 11.49;
      
      setRows(prev => prev.map(row => {
        if (row.id !== rowId) return row;
        const rawPrice = priceData.purchase_price || 0;
        const inkopspris = priceData.currency === "EUR" ? Math.round(rawPrice * EUR_TO_SEK) : rawPrice;
        const frakt = priceData.freight_cost || 0;
        const kemskatt = priceData.chemical_tax || 0;
        
        // Suggest selling price with ~15% margin if not already set
        let suggestedPris = row.pris;
        if (!suggestedPris && inkopspris > 0) {
          suggestedPris = Math.ceil((inkopspris + frakt + kemskatt) * 1.25 * 1.15 / 100) * 100;
        }
        
        return {
          ...row,
          inkopspris,
          frakt,
          kemskatt,
          pris: suggestedPris,
        };
      }));
    } catch (error) {
      console.error("Error fetching price:", error);
      // Price stays at 0
    }
  };

  // Update row field
  const updateRow = (rowId, field, value) => {
    setRows(prev => prev.map(row => {
      if (row.id !== rowId) return row;
      return { ...row, [field]: value };
    }));
  };

  // Add new row
  const addRow = () => {
    const newId = Math.max(...rows.map(r => r.id), 0) + 1;
    setRows(prev => [...prev, createEmptyRow(newId)]);
  };

  // Remove row
  const removeRow = (rowId) => {
    if (rows.length <= 1) {
      // Clear the row instead of removing
      setRows([createEmptyRow(1)]);
      return;
    }
    setRows(prev => prev.filter(row => row.id !== rowId));
  };

  // Clear all rows
  const clearAll = async () => {
    setRows([createEmptyRow(1)]);
    try {
      await axios.delete(`${API}/quotes/my`);
      toast.success("Offerten rensad");
    } catch (error) {
      console.error("Error clearing quote:", error);
    }
  };

  // Copy quote to clipboard
  const copyQuote = () => {
    // Filter out empty rows (rows without a product selected)
    const filledRows = rows.filter(row => row.productId);
    
    if (filledRows.length === 0) {
      toast.error("Inga produkter att kopiera");
      return;
    }
    
    // Format: Antal st, Märke, Kategori, Artikelnr, Tot. Utpris
    const lines = filledRows.map(row => {
      const antal = parseInt(row.antal) || 0;
      const pris = parseFloat(row.pris) || 0;
      const totUtpris = antal * pris;
      
      const parts = [
        `${antal} st`,
        row.brand || "",
        row.kategori || "",
        row.articleNumber || "",
        `${formatCurrency(totUtpris)}`
      ].filter(p => p); // Remove empty parts
      
      return parts.join(", ");
    });
    
    const text = lines.join("\n");
    
    // Try modern clipboard API first, then fallback
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        toast.success("Offert kopierad till urklipp");
      }).catch(() => {
        fallbackCopyToClipboard(text);
      });
    } else {
      fallbackCopyToClipboard(text);
    }
  };
  
  // Fallback copy method for non-secure contexts
  const fallbackCopyToClipboard = (text, successMsg = "Offert kopierad till urklipp") => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      toast.success(successMsg);
    } catch (err) {
      toast.error("Kunde inte kopiera");
    }
    
    document.body.removeChild(textArea);
  };

  // Copy quote with total only (no per-product prices)
  const copyQuoteTotal = () => {
    const filledRows = rows.filter(row => row.productId);
    
    if (filledRows.length === 0) {
      toast.error("Inga produkter att kopiera");
      return;
    }
    
    // Format: Antal st, Märke, Kategori, Artikelnr (without per-product price)
    const lines = filledRows.map(row => {
      const antal = parseInt(row.antal) || 0;
      
      const parts = [
        `${antal} st`,
        row.brand || "",
        row.kategori || "",
        row.articleNumber || ""
      ].filter(p => p);
      
      return parts.join(", ");
    });
    
    // Add total at the end
    lines.push("");
    lines.push(`Totalt: ${formatCurrency(grandTotals.summaUtpris)}`);
    
    const text = lines.join("\n");
    const successMsg = "Offert med totalsumma kopierad";
    
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        toast.success(successMsg);
      }).catch(() => {
        fallbackCopyToClipboard(text, successMsg);
      });
    } else {
      fallbackCopyToClipboard(text, successMsg);
    }
  };

  // Calculate row totals
  const calculateRowTotals = (row) => {
    const antal = parseInt(row.antal) || 0;
    const inkopspris = parseFloat(row.inkopspris) || 0;
    const frakt = parseFloat(row.frakt) || 0;
    const kemskatt = parseFloat(row.kemskatt) || 0;
    const pris = parseFloat(row.pris) || 0;
    
    const totInkopExklMoms = antal * (inkopspris + frakt + kemskatt);
    const totProdInklMoms = totInkopExklMoms * 1.25; // 25% VAT
    const totUtpris = antal * pris;
    const marginal = totUtpris > 0 ? ((totUtpris - totProdInklMoms) / totUtpris) * 100 : 0;
    
    return {
      totInkopExklMoms,
      totProdInklMoms,
      totUtpris,
      marginal,
    };
  };

  // Calculate grand totals
  const grandTotals = rows.reduce((acc, row) => {
    const totals = calculateRowTotals(row);
    return {
      summaInkop: acc.summaInkop + totals.totInkopExklMoms,
      summaMedMoms: acc.summaMedMoms + totals.totProdInklMoms,
      summaUtpris: acc.summaUtpris + totals.totUtpris,
    };
  }, { summaInkop: 0, summaMedMoms: 0, summaUtpris: 0 });

  const totalMarginal = grandTotals.summaUtpris > 0 
    ? ((grandTotals.summaUtpris - grandTotals.summaMedMoms) / grandTotals.summaUtpris) * 100 
    : 0;

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('sv-SE', { 
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value) + ' kr';
  };

  if (loading) {
    return (
      <Card className="w-full" data-testid="quote-calculator">
        <CardContent className="p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground mt-2">Laddar offert...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full" data-testid="quote-calculator">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-primary" />
            <CardTitle className="font-heading text-lg uppercase">Räkna Offert</CardTitle>
            {currentQuoteName && (
              <span className="text-sm text-muted-foreground">
                — {currentQuoteName}
              </span>
            )}
            {saving && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Sparar...
              </span>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Save Quote */}
            <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" onClick={() => setQuoteName(currentQuoteName)} data-testid="save-quote-btn">
                  <Save className="h-4 w-4 mr-1" />
                  Spara offert
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Spara offert</DialogTitle>
                  <DialogDescription>
                    Ge offerten ett namn för att kunna hämta den senare.
                  </DialogDescription>
                </DialogHeader>
                <Input
                  placeholder="Namn på offert..."
                  value={quoteName}
                  onChange={(e) => setQuoteName(e.target.value)}
                  data-testid="quote-name-input"
                />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>Avbryt</Button>
                  <Button onClick={saveQuoteWithName} data-testid="confirm-save-quote">
                    {currentQuoteId ? "Uppdatera" : "Spara"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            {/* Load Quote */}
            <Dialog open={loadDialogOpen} onOpenChange={(open) => {
              setLoadDialogOpen(open);
              if (open) loadSavedQuotes();
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="load-quote-btn">
                  <FolderOpen className="h-4 w-4 mr-1" />
                  Hämta offert
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Sparade offerter</DialogTitle>
                  <DialogDescription>
                    Välj en sparad offert att hämta.
                  </DialogDescription>
                </DialogHeader>
                <div className="max-h-[300px] overflow-y-auto space-y-2">
                  {savedQuotes.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Inga sparade offerter
                    </p>
                  ) : (
                    savedQuotes.map(quote => (
                      <div
                        key={quote.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                        onClick={() => loadSavedQuote(quote)}
                        data-testid={`saved-quote-${quote.id}`}
                      >
                        <div>
                          <p className="font-medium">{quote.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {quote.rows?.length || 0} produkter • {new Date(quote.updated_at || quote.created_at).toLocaleDateString('sv-SE')}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={(e) => deleteSavedQuote(quote.id, e)}
                          data-testid={`delete-quote-${quote.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Copy dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="copy-quote-dropdown">
                  <Copy className="h-4 w-4 mr-1" />
                  Kopiera
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={copyQuote} data-testid="copy-quote-per-product">
                  Kopiera offert pris/produkt
                </DropdownMenuItem>
                <DropdownMenuItem onClick={copyQuoteTotal} data-testid="copy-quote-total">
                  Kopiera offert med Tot. utpris
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Add row */}
            <Button variant="outline" size="sm" onClick={addRow} data-testid="add-quote-row">
              <Plus className="h-4 w-4 mr-1" />
              Lägg till rad
            </Button>
            
            {/* Clear - moved to the right */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" data-testid="clear-quote">
                  <X className="h-4 w-4 mr-1" />
                  Rensa
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    Rensa offerten?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Är du säker på att du vill rensa hela offerten? Alla rader kommer att tas bort permanent.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Avbryt</AlertDialogCancel>
                  <AlertDialogAction onClick={clearAll} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Rensa allt
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Din offert sparas automatiskt. Använd "Spara offert" för att namnge och hämta den senare.
        </p>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {/* Table */}
        <table className="w-full text-sm border-collapse min-w-[1200px]">
          <thead>
            <tr className="bg-muted/50">
              <th className="p-2 text-left font-medium border border-border w-16 bg-green-500/20">Antal</th>
              <th className="p-2 text-center font-medium border border-border w-8"></th>
              <th className="p-2 text-left font-medium border border-border w-24">Märke</th>
              <th className="p-2 text-left font-medium border border-border w-28">Kategori</th>
              <th className="p-2 text-left font-medium border border-border bg-green-500/20 min-w-[160px]">Artikelnr</th>
              <th className="p-2 text-right font-medium border border-border w-24">Inköpspris</th>
              <th className="p-2 text-right font-medium border border-border w-20">Frakt</th>
              <th className="p-2 text-right font-medium border border-border w-20">Kemskatt</th>
              <th className="p-2 text-right font-medium border border-border w-28">Tot. Inköp exkl. moms</th>
              <th className="p-2 text-right font-medium border border-border w-28">Tot. Prod. inkl. moms</th>
              <th className="p-2 text-right font-medium border border-border bg-green-500/20 w-24">Pris</th>
              <th className="p-2 text-right font-medium border border-border w-24">Tot. Utpris</th>
              <th className="p-2 text-right font-medium border border-border w-20">Marginal</th>
              <th className="p-2 text-center font-medium border border-border w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const totals = calculateRowTotals(row);
              const hasProduct = !!row.productId;
              
              return (
                <tr key={row.id} className="hover:bg-muted/30" data-testid={`quote-row-${row.id}`}>
                  {/* Antal - Editable */}
                  <td className="p-1 border border-border bg-green-500/10">
                    <Input
                      type="number"
                      min="1"
                      value={row.antal}
                      onChange={(e) => updateRow(row.id, 'antal', parseInt(e.target.value) || 1)}
                      className="h-8 text-center w-full bg-transparent border-0 focus-visible:ring-1"
                      data-testid={`quote-antal-${row.id}`}
                    />
                  </td>
                  
                  {/* Multiplier symbol */}
                  <td className="p-1 border border-border text-center text-muted-foreground">×</td>
                  
                  {/* Märke - From product */}
                  <td className="p-1 border border-border">
                    <span className="px-2 text-sm truncate block">{row.brand || "—"}</span>
                  </td>
                  
                  {/* Kategori - From product */}
                  <td className="p-1 border border-border">
                    <span className="px-2 text-sm truncate block">{row.kategori || "—"}</span>
                  </td>
                  
                  {/* Artikelnr - Product search */}
                  <td className="p-1 border border-border bg-green-500/10 relative" data-quote-search>
                    {hasProduct && openPopover !== row.id ? (
                      <div 
                        className="w-full h-8 px-2 flex items-center font-mono text-sm cursor-pointer select-all"
                        onDoubleClick={(e) => {
                          const selection = window.getSelection();
                          const range = document.createRange();
                          range.selectNodeContents(e.target);
                          selection.removeAllRanges();
                          selection.addRange(range);
                        }}
                        onClick={() => {
                          setOpenPopover(row.id);
                          setSearchQuery(row.articleNumber);
                          handleSearch(row.articleNumber);
                        }}
                      >
                        <span className="truncate">{row.articleNumber}</span>
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="flex items-center">
                          <Search className="h-3 w-3 absolute left-2 text-muted-foreground pointer-events-none" />
                          <input
                            type="text"
                            className="w-full h-8 pl-7 pr-2 bg-transparent border-0 outline-none text-sm placeholder:text-muted-foreground"
                            placeholder="Sök produkt..."
                            autoFocus={openPopover === row.id}
                            value={openPopover === row.id ? searchQuery : ""}
                            onFocus={() => setOpenPopover(row.id)}
                            onChange={(e) => {
                              setOpenPopover(row.id);
                              handleSearch(e.target.value);
                            }}
                            data-testid={`quote-product-${row.id}`}
                          />
                        </div>
                      </div>
                    )}
                    {/* Dropdown results */}
                    {openPopover === row.id && (
                      <div className="absolute top-full left-0 z-50 w-[400px] bg-popover border border-border rounded-md shadow-lg mt-1 max-h-[250px] overflow-y-auto">
                        {searchQuery.length < 2 ? (
                          <p className="p-3 text-sm text-muted-foreground">Skriv minst 2 tecken för att söka</p>
                        ) : searching ? (
                          <p className="p-3 text-sm text-muted-foreground">Söker...</p>
                        ) : searchResults.length === 0 ? (
                          <p className="p-3 text-sm text-muted-foreground">Inga produkter hittades</p>
                        ) : (
                          searchResults.map((product) => {
                            const displayArticle = product.artikelnummer || product.article_number || product.ean || "";
                            const displayBrand = product.tillverkare || product.brand || "Okänt märke";
                            const displayKategori = product.kategori || product.category || "";
                            return (
                              <div
                                key={product.id}
                                className="px-3 py-2 hover:bg-accent cursor-pointer"
                                onClick={() => handleSelectProduct(row.id, product)}
                              >
                                <span className="font-medium">{displayArticle}</span>
                                <span className="text-xs text-muted-foreground ml-2">
                                  {displayBrand}{displayKategori && ` · ${displayKategori}`}
                                </span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </td>
                  
                  {/* Inköpspris - From system */}
                  <td className="p-1 border border-border text-right">
                    <span className="px-2">{formatCurrency(row.inkopspris)}</span>
                  </td>
                  
                  {/* Frakt - From system */}
                  <td className="p-1 border border-border text-right">
                    <span className="px-2">{row.frakt > 0 ? formatCurrency(row.frakt) : "—"}</span>
                  </td>
                  
                  {/* Kemskatt - From system */}
                  <td className="p-1 border border-border text-right">
                    <span className="px-2">{formatCurrency(row.kemskatt)}</span>
                  </td>
                  
                  {/* Tot. Inköp exkl. moms - Calculated */}
                  <td className="p-1 border border-border text-right">
                    <span className="px-2">{formatCurrency(totals.totInkopExklMoms)}</span>
                  </td>
                  
                  {/* Tot. Prod. inkl. moms - Calculated */}
                  <td className="p-1 border border-border text-right">
                    <span className="px-2">{formatCurrency(totals.totProdInklMoms)}</span>
                  </td>
                  
                  {/* Pris (till kund) - Editable */}
                  <td className="p-1 border border-border bg-green-500/10">
                    <Input
                      type="number"
                      min="0"
                      step="100"
                      value={row.pris || ""}
                      onChange={(e) => updateRow(row.id, 'pris', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      className="h-8 text-right w-full bg-transparent border-0 focus-visible:ring-1"
                      data-testid={`quote-pris-${row.id}`}
                    />
                  </td>
                  
                  {/* Tot. Utpris - Calculated */}
                  <td className="p-1 border border-border text-right font-medium">
                    <span className="px-2">{formatCurrency(totals.totUtpris)}</span>
                  </td>
                  
                  {/* Marginal - Calculated */}
                  <td className={cn(
                    "p-1 border border-border text-right font-medium",
                    totals.marginal > 15 ? "text-green-500" : totals.marginal > 0 ? "text-yellow-500" : "text-red-500"
                  )}>
                    <span className="px-2">{hasProduct && row.pris > 0 ? `${totals.marginal.toFixed(1)}%` : "—"}</span>
                  </td>
                  
                  {/* Delete button */}
                  <td className="p-1 border border-border text-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-destructive"
                      onClick={() => removeRow(row.id)}
                      data-testid={`quote-delete-${row.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          
          {/* Summary footer */}
          <tfoot>
            <tr className="bg-muted/80 font-medium">
              <td colSpan={8} className="p-2 border border-border text-right">
                Summa pris (inkl frakt & kem.skatt):
              </td>
              <td className="p-2 border border-border text-right">
                {formatCurrency(grandTotals.summaInkop)}
              </td>
              <td className="p-2 border border-border text-right">
                {formatCurrency(grandTotals.summaMedMoms)}
              </td>
              <td className="p-2 border border-border"></td>
              <td className="p-2 border border-border text-right font-bold text-primary">
                {formatCurrency(grandTotals.summaUtpris)}
              </td>
              <td className={cn(
                "p-2 border border-border text-right font-bold",
                totalMarginal > 15 ? "text-green-500" : totalMarginal > 0 ? "text-yellow-500" : "text-red-500"
              )}>
                {grandTotals.summaUtpris > 0 ? `${totalMarginal.toFixed(1)}%` : "—"}
              </td>
              <td className="p-2 border border-border"></td>
            </tr>
            <tr className="bg-muted/50 text-xs text-muted-foreground">
              <td colSpan={8} className="p-2 border border-border"></td>
              <td className="p-2 border border-border text-right">Exkl. moms</td>
              <td className="p-2 border border-border text-right">Med moms (25%)</td>
              <td className="p-2 border border-border"></td>
              <td className="p-2 border border-border text-right">Pris till utkund</td>
              <td className="p-2 border border-border text-right">Faktisk marginal</td>
              <td className="p-2 border border-border"></td>
            </tr>
          </tfoot>
        </table>

        {/* Help text */}
        <p className="text-xs text-muted-foreground mt-4">
          Gröna fält är redigerbara. Välj produkt för att hämta inköpspris, frakt och kemskatt från systemet.
          Fraktkostnaden hämtas från senaste leverans. Marginalen beräknas som (Utpris - Kostnad inkl. moms) / Utpris.
        </p>
      </CardContent>
    </Card>
  );
}
